import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import type { ActionItem } from "@/pages/Dashboard/dashboard.types";

/**
 * Home aggregators — endpoints composed specifically for the redesigned Home /
 * Dashboard so widgets don't N+1 across the platform.
 *
 * `GET /home/needs-you` returns the server-ranked action queue (urgent →
 * soonest) backing the "Needs you" widget. Each `ActionItem` opens the
 * underlying document or deal; severity drives its tone (urgent red / nudge
 * orange / normal blue). Empty `data` → the widget renders its "all caught up"
 * empty state (it is always shown).
 *
 * `GET /home/properties` returns the user's properties tagged by their
 * relationship to each one (owned / on a deal / listed / saved), backing the
 * "Your properties" rail widget. The endpoint may not be deployed yet — the
 * widget degrades cleanly (empty state) when it 404s.
 *
 * `GET /home/representation` returns rep-agreement deals that DON'T have a
 * parent pursuit yet (each implicitly needs action), backing the
 * "Representation" rail widget. Same degrade-cleanly contract — when it 404s
 * (not deployed yet) the widget renders its empty state.
 */

/** A property the user has some relationship to (owned / on a deal / listed / saved). */
export interface HomeProperty {
  id: string;
  name: string;
  address: string;
  assetRef?: string | null;
  relationship: "owned" | "on_deal" | "listed" | "saved";
}

/** The four rep-agreement enums a representation deal can carry. */
export type RepType =
  | "BUYER_REP_AGREEMENT"
  | "SELLER_REP_AGREEMENT"
  | "TENANT_REP_AGREEMENT"
  | "LANDLORD_REP_AGREEMENT";

/**
 * One rep-agreement deal without a parent pursuit yet. `side` flips demand
 * (buyer/tenant) vs supply (seller/landlord); `signal` carries the small
 * right-aligned pill ("3 matches" / "2 tours" / "Set up") and its tone.
 */
export interface RepresentationItem {
  id: string;
  name: string;
  repType: RepType;
  side: "demand" | "supply";
  /** One-line meta, e.g. "Buyer rep · Office acquisition · St. Louis". */
  meta: string;
  signal: { label: string; tone: "matches" | "tours" | "setup" };
}

export const homeApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "homeApi",
    baseUrl: "/api/home",
  }),
  reducerPath: "homeApi",
  tagTypes: ["NeedsYou", "HomeProperties", "Representation"],
  endpoints: (builder) => ({
    getNeedsYou: builder.query<{ data: ActionItem[] }, void>({
      query: () => "/needs-you",
      keepUnusedDataFor: 30,
      providesTags: ["NeedsYou"],
    }),
    getHomeProperties: builder.query<{ data: HomeProperty[] }, void>({
      query: () => "/properties",
      keepUnusedDataFor: 60,
      providesTags: ["HomeProperties"],
    }),
    getRepresentation: builder.query<{ data: RepresentationItem[] }, void>({
      query: () => "/representation",
      keepUnusedDataFor: 60,
      providesTags: ["Representation"],
    }),
  }),
});

export const {
  useGetNeedsYouQuery,
  useGetHomePropertiesQuery,
  useGetRepresentationQuery,
} = homeApi;
